// Profile `type` enum used by the server. The server guarantees every
// URL-bearing profile has a `type` (known platforms plus a generic `Web`), so
// platform detection reads `type` directly rather than sniffing the URL.
const ProfileTypes = {
  GitHub: 1,
  YouTube: 4,
  X: 5,
  Web: 6,
  LinkedIn: 7,
  HackerNews: 8,
  Lobsters: 9,
  Wikipedia: 10,
  Reddit: 11,
};

// Badge/icon key for each known platform `type`. Anything else with a URL is a
// generic website.
const PLATFORM_BY_TYPE = {
  [ProfileTypes.GitHub]: 'github',
  [ProfileTypes.YouTube]: 'youtube',
  [ProfileTypes.X]: 'x',
  [ProfileTypes.LinkedIn]: 'linkedin',
  [ProfileTypes.HackerNews]: 'hackernews',
  [ProfileTypes.Lobsters]: 'lobsters',
  [ProfileTypes.Wikipedia]: 'wikipedia',
  [ProfileTypes.Reddit]: 'reddit',
};

// Clean domain/path label for badge tooltips: protocol, leading `www.` and
// trailing slash stripped (e.g. `github.com/jkreps`, `jaykreps.com`).
function cleanUrlLabel(url) {
  return (url || '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
}

// Platform detection for a profile. The server already resolves each known
// platform's canonical url from its username. Name-only profiles resolve to no
// URL (and are not shown as badges).
export function profileMeta(profile) {
  const url = (profile && profile.url) || '';
  if (!url) return { platform: null, url: '' };
  return { platform: PLATFORM_BY_TYPE[profile && profile.type] || 'web', url };
}

// All profiles that resolve to a URL, each with a clean domain/path tooltip.
export function urlBadges(profiles) {
  return (profiles || [])
    .map(profileMeta)
    .filter((meta) => meta.url)
    .map((meta) => ({
      ...meta,
      tip: cleanUrlLabel(meta.url),
    }));
}

// Display name derived from the profiles: first profile with a name, else
// first with a username as `@username`.
export function deriveName(profiles) {
  const named = (profiles || []).find((p) => p.name);
  if (named) return named.name;
  const withUsername = (profiles || []).find((p) => p.username);
  return withUsername ? `@${withUsername.username}` : 'Unknown author';
}

// Primary handle: first profile with a username, as `@username`.
export function deriveHandle(profiles) {
  const withUsername = (profiles || []).find((p) => p.username);
  return withUsername ? `@${withUsername.username}` : '';
}

// Portrait: first profile with an image.
export function derivePortraitUrl(profiles) {
  const withImage = (profiles || []).find((p) => p.imageUrl);
  return withImage ? withImage.imageUrl : null;
}

// Trim the editable string fields of a draft profile and drop the empty ones,
// preserving any other fields (type, id, ...) untouched.
export function cleanProfile(profile) {
  const cleaned = { ...profile };
  ['name', 'username', 'url', 'imageUrl'].forEach((field) => {
    const value = (cleaned[field] || '').trim();
    if (value) cleaned[field] = value;
    else delete cleaned[field];
  });
  return cleaned;
}

export function isEmptyProfile(profile) {
  return !profile.name && !profile.username && !profile.url && !profile.imageUrl;
}
